import { useEffect, useMemo, useState } from 'react';
import { copy } from './i18n';
import { previewHtml, syntheticProject } from './synthetic';
import type { InterviewAnswer, Locale, Stage } from './types';
import './styles.css';

const stages: Stage[] = ['interview', 'research', 'trail', 'preview', 'publish'];

export function matchesDigestConfirmation(digest: string, confirmation: string): boolean {
  return /^[a-f0-9]{64}$/.test(digest) && confirmation.trim() === digest.slice(-8);
}

function Progress({ current, locale }: { current: Stage; locale: Locale }) {
  const t = copy[locale];
  const currentIndex = stages.indexOf(current);
  return (
    <ol className="progress" aria-label={t.progressLabel}>
      {stages.map((stage, index) => (
        <li key={stage} className={index <= currentIndex ? 'complete' : ''} aria-current={stage === current ? 'step' : undefined}>
          <span>{index < currentIndex ? '✓' : index + 1}</span>{t[stage]}
        </li>
      ))}
    </ol>
  );
}

function Metric({ value, label, detail }: { value: string | number; label: string; detail: string }) {
  return <div className="metric"><strong>{value}</strong><span>{label}</span><small>{detail}</small></div>;
}

function Interview({ locale, onSaved }: { locale: Locale; onSaved: (answers: InterviewAnswer) => void }) {
  const [answers, setAnswers] = useState(syntheticProject.answers);
  const t = copy[locale];
  const update = <K extends keyof InterviewAnswer>(key: K, value: InterviewAnswer[K]) => setAnswers((old) => ({ ...old, [key]: value }));
  return (
    <form className="panel interview" onSubmit={(event) => { event.preventDefault(); onSaved(answers); }}>
      <div className="section-heading"><div><span className="eyebrow">{t.interviewEyebrow}</span><h2>{t.interviewTitle}</h2></div><span className="time">{t.interviewTime}</span></div>
      <label><span>{t.paceQuestion}</span>
        <select value={answers.pace} onChange={(event) => update('pace', event.target.value as InterviewAnswer['pace'])}>
          <option value="relaxed">{t.relaxed}</option><option value="balanced">{t.balanced}</option><option value="packed">{t.packed}</option>
        </select>
      </label>
      <label><span>{t.prioritiesQuestion}</span><textarea value={answers.priorities} onChange={(event) => update('priorities', event.target.value)} rows={3} /></label>
      <label><span>{t.constraintsQuestion}</span><textarea value={answers.constraints} onChange={(event) => update('constraints', event.target.value)} rows={3} /></label>
      <div className="form-footer"><p>{t.aiCommit}</p><button className="primary" type="submit">{t.answer}</button></div>
    </form>
  );
}

function Preview({ locale, onApprove }: { locale: Locale; onApprove: () => void }) {
  const t = copy[locale];
  const [confirmation, setConfirmation] = useState('');
  return (
    <section className="panel preview-panel" aria-labelledby="preview-title">
      <div className="section-heading"><div><span className="eyebrow">{t.previewEyebrow}</span><h2 id="preview-title">{t.previewTitle}</h2></div><span className="preview-status">{t.deployed}</span></div>
      <div className="preview-frame"><iframe title={t.previewFrameTitle} srcDoc={previewHtml(locale)} sandbox="" /></div>
      <div className="privacy-note"><span aria-hidden="true">◉</span><p><strong>{t.localMeansLocal}</strong><br />{t.privateNote}</p></div>
      <div className="digest-box"><div><span>{t.digestLabel}</span><code>{syntheticProject.digest}</code></div><label><span>{t.confirmationLabel}</span><input value={confirmation} onChange={(event) => setConfirmation(event.target.value.trim())} maxLength={8} autoComplete="off" /></label><button className="primary" disabled={!matchesDigestConfirmation(syntheticProject.digest, confirmation)} onClick={onApprove}>{t.approve}</button></div>
    </section>
  );
}

export default function App() {
  const [locale, setLocale] = useState<Locale>('en');
  const [notice, setNotice] = useState('');
  const t = copy[locale];
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'he' ? 'rtl' : 'ltr';
  }, [locale]);
  const snapshot = useMemo(() => new Intl.DateTimeFormat(locale === 'he' ? 'he-IL' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }).format(new Date(syntheticProject.snapshotAt)), [locale]);
  return (
    <div className="app" dir={locale === 'he' ? 'rtl' : 'ltr'} lang={locale}>
      <header className="topbar">
        <a className="brand" href="#main" aria-label={t.product}><span className="brand-mark">T</span><span><strong>{t.product}</strong><small>{t.workspace}</small></span></a>
        <div className="top-actions"><span className="private-badge">● {t.private}</span><button className="language" onClick={() => setLocale(locale === 'en' ? 'he' : 'en')}>{locale === 'en' ? 'עברית' : 'English'}</button><button className="avatar" aria-label={t.account}>YS</button></div>
      </header>
      <main id="main">
        <div className="hero"><div><span className="eyebrow">{t.fixtureName}</span><h1>{t.heading}</h1><p>{t.subheading}</p></div><div className="offer"><span>{t.offerDuration}</span><strong>$99</strong><small>{t.offerOffline}</small></div></div>
        <Progress current={syntheticProject.stage} locale={locale} />
        {notice && <div className="notice" role="status">✓ {notice}<button aria-label={t.dismiss} onClick={() => setNotice('')}>×</button></div>}
        <div className="layout">
          <div className="main-column">
            <Interview locale={locale} onSaved={() => setNotice(t.savedNotice)} />
            <Preview locale={locale} onApprove={() => setNotice(t.approvedNotice)} />
          </div>
          <aside>
            <section className="panel metrics" aria-labelledby="credits"><div className="section-heading"><h2 id="credits">{t.credits}</h2><span className="healthy">{t.healthy}</span></div><div className="metric-grid"><Metric value={syntheticProject.previewCredits} label={t.previewUpdates} detail={t.previewRemaining} /><Metric value={syntheticProject.fullCredits} label={t.fullUpdates} detail={t.fullRemaining} /><Metric value={`${syntheticProject.devices}/6`} label={t.devices} detail={t.deviceUsage} /></div><p className="settlement">{t.settlement}</p></section>
            <section className="panel research" aria-labelledby="research"><span className="eyebrow">{t.snapshot}</span><h2 id="research">{t.researchTitle}</h2><time>{snapshot} UTC</time><ul>{[t.unresolvedHours, t.unresolvedAccess].map((item) => <li key={item}><span>!</span>{item}</li>)}</ul><button className="secondary">{t.reviewSources} <span>→</span></button></section>
            <section className="panel offline"><div className="offline-icon">↓</div><div><strong>{t.offlineTitle}</strong><p>{t.offlineBody}</p></div></section>
          </aside>
        </div>
      </main>
      <footer><span>{t.footerFixture}</span><span>{t.footerPrivacy}</span></footer>
    </div>
  );
}
